import { useContext, useEffect, useMemo, useState } from "react";
import { Alert, Box, Button } from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { ptBR, esES as es, enUS as en } from "@mui/x-data-grid/locales";
import axios from "axios";
import { useTranslation } from "react-i18next";

import { AuthContext } from "../../contexts/AuthContext";
import { CONFIG } from "../../services/config";
import { FormInputText } from "../../components/form/FormInputText";
import MyModal from "../../components/MyModal";

type Props = {
  campaign: any;
  onClose: () => void;
};

interface ParticipantRow {
  id: string;
  email: string;
}

const DEFAULT_PAGE_SIZE = 25;

const addParticipantEmails = (participants: any, emails: Set<string>) => {
  if (!Array.isArray(participants)) {
    return;
  }

  participants.forEach((participant: any) => {
    if (typeof participant === "string") {
      emails.add(participant);
      return;
    }

    const participantEmail =
      participant?.email ||
      participant?.participantEmail ||
      participant?.userEmail ||
      participant?.customerEmail;

    if (participantEmail) {
      emails.add(String(participantEmail));
    }
  });
};

const extractEmailsFromCampaign = (campaign: any): string[] => {
  const emails = new Set<string>();

  if (Array.isArray(campaign)) {
    addParticipantEmails(campaign, emails);
  }

  if (campaign?.email) {
    emails.add(String(campaign.email));
  }

  if (Array.isArray(campaign?.rules)) {
    campaign.rules.forEach((rule: any) => {
      if (rule?.email) {
        emails.add(String(rule.email));
      }
    });
  }

  addParticipantEmails(campaign?.participants, emails);
  addParticipantEmails(campaign?.content, emails);
  addParticipantEmails(campaign?.data, emails);
  addParticipantEmails(campaign?.data?.content, emails);
  addParticipantEmails(campaign?.data?.participants, emails);

  return Array.from(emails).filter(Boolean);
};

const getCampaignId = (campaign: any) => {
  const campaignWithMongoId = campaign as any;

  return campaign?.id || campaignWithMongoId["_id"] || campaign?.campaignId;
};

const buildParticipantRows = (campaignData: any): ParticipantRow[] => {
  const emails = extractEmailsFromCampaign(campaignData);

  return emails.map((email, index) => ({
    id: `${email}-${index}`,
    email,
  }));
};

export default function ModalViewCampaign({ campaign, onClose }: Props) {
  const { t, i18n } = useTranslation();
  const {
    tokenHeaders,
    country: { countryCode },
  } = useContext(AuthContext);

  const campaignId = getCampaignId(campaign);
  const localRows = useMemo(() => buildParticipantRows(campaign), [campaign]);

  const [searchValue, setSearchValue] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [allRows, setAllRows] = useState<ParticipantRow[]>(localRows);
  const [rows, setRows] = useState<ParticipantRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const localeByLanguage: { [language: string]: any } = {
    ptbr: ptBR,
    en,
    es,
  };

  const currentLocale = localeByLanguage[i18n.language] ?? ptBR;
  const localeText =
    currentLocale.components.MuiDataGrid.defaultProps.localeText;

  const columns: GridColDef[] = [
    {
      field: "email",
      headerName: t("ottoTrialPeriod.columns.participants"),
      flex: 1,
      minWidth: 220,
      sortable: false,
      disableColumnMenu: true,
      headerClassName: "header-style",
    },
  ];

  const applyLocalPagination = (
    sourceRows: ParticipantRow[],
    filterValue: string,
    nextPage: number,
    nextSize: number,
  ) => {
    const normalizedFilter = filterValue.trim().toLowerCase();
    const filtered = sourceRows.filter((row) =>
      row.email.toLowerCase().includes(normalizedFilter),
    );
    const start = nextPage * nextSize;
    const paged = filtered.slice(start, start + nextSize);

    setRows(paged);
    setTotal(filtered.length);
  };

  const applyCurrentView = (
    sourceRows = allRows,
    filterValue = appliedSearch,
    nextPage = page,
    nextSize = pageSize,
  ) => {
    applyLocalPagination(sourceRows, filterValue, nextPage, nextSize);
  };

  const fetchCampaignById = async () => {
    setLoading(true);
    setErrorMessage("");

    if (!campaignId) {
      setAllRows(localRows);
      applyLocalPagination(localRows, appliedSearch, 0, pageSize);
      setLoading(false);
      return;
    }

    const requestHeaders = {
      ...tokenHeaders.headers,
      countryCode: countryCode || tokenHeaders.headers.countryCode,
      countrycode: countryCode || tokenHeaders.headers.countryCode,
      "Content-Type": "application/json",
    };

    try {
      const response = await axios.get(
        `${CONFIG.API_URL}temporaryAccess/${campaignId}/participants`,
        {
          headers: requestHeaders,
        },
      );

      const fetchedRows = buildParticipantRows(response.data);
      setAllRows(fetchedRows);
      applyLocalPagination(fetchedRows, appliedSearch, 0, pageSize);
    } catch (error: any) {
      if (localRows.length) {
        setAllRows(localRows);
        applyLocalPagination(localRows, appliedSearch, 0, pageSize);
      } else {
        setAllRows([]);
        setRows([]);
        setTotal(0);
        setErrorMessage(
          error?.response?.data?.message ||
            t("ottoTrialPeriod.view.errorLoadParticipants"),
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    setPage(0);
    setAppliedSearch(searchValue);
    applyCurrentView(allRows, searchValue, 0, pageSize);
  };

  const handleClear = async () => {
    setSearchValue("");
    setAppliedSearch("");
    setPage(0);
    applyCurrentView(allRows, "", 0, pageSize);
  };

  useEffect(() => {
    fetchCampaignById();
  }, [campaignId]);

  useEffect(() => {
    if (!campaignId) {
      setAllRows(localRows);
      applyLocalPagination(localRows, appliedSearch, page, pageSize);
    }
  }, [localRows]);

  return (
    <MyModal
      title={t("ottoTrialPeriod.view.title")}
      modalChange={onClose}
      maxWidth="70rem"
      disableDivider
    >
      <Box sx={{ borderTop: "1px solid var(--gray-100)", mt: 1.5, pt: 2 }}>
        <Box sx={{ marginBottom: 1 }}>
          <span style={{ fontSize: 12 }}>
            {t("ottoTrialPeriod.view.searchDescription")}
          </span>
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "stretch", sm: "flex-start" },
            gap: { xs: 1.5, sm: 2.5 },
            marginBottom: 2,
          }}
        >
          <Box sx={{ width: { xs: "100%", sm: 300 } }}>
            <FormInputText
              id="viewParticipantsSearch"
              type="text"
              label={t("ottoTrialPeriod.view.searchInputText")}
              value={searchValue}
              onChange={(event: any) => setSearchValue(event.target.value)}
              onKeyDown={(event: any) => {
                if (event.key === "Enter") {
                  handleSearch();
                }
              }}
              sx={{ marginBottom: 0 }}
            />
          </Box>

          <Box
            sx={{
              display: "flex",
              gap: 2,
              width: { xs: "100%", sm: "auto" },
              "& .MuiButton-root": {
                height: 40,
                minWidth: 116,
              },
            }}
          >
            <Button variant="contained" onClick={handleSearch}>
              {t("button.Search")}
            </Button>
            <Button
              sx={{ color: "#00AFF2" }}
              variant="outlined"
              onClick={handleClear}
            >
              {t("ottoTrialPeriod.list.cleanButtonText")}
            </Button>
          </Box>
        </Box>

        {errorMessage && (
          <Box sx={{ marginBottom: 2 }}>
            <Alert severity="error">{errorMessage}</Alert>
            <Alert severity="warning" sx={{ marginTop: 1 }}>
              {t("alert.tryAgain")}
            </Alert>
          </Box>
        )}

        <Box
          sx={{
            width: "100%",
            "& .header-style": {
              backgroundColor: "var(--gray-100)",
              minHeight: "4rem",
            },
          }}
        >
          <DataGrid
            rowCount={total}
            localeText={{
              ...localeText,
              noRowsLabel: t("ottoTrialPeriod.view.emptyMessage"),
            }}
            getRowId={(row) => row.id}
            rowHeight={52}
            getRowHeight={() => "auto"}
            rows={rows}
            columns={columns}
            loading={loading}
            pageSizeOptions={[25, 50, 75, 100]}
            paginationModel={{ page, pageSize }}
            paginationMode="server"
            onPaginationModelChange={(model) => {
              if (model.pageSize !== pageSize) {
                setPageSize(model.pageSize);
                setPage(0);
                applyCurrentView(allRows, appliedSearch, 0, model.pageSize);
              } else {
                setPage(model.page);
                applyCurrentView(allRows, appliedSearch, model.page, pageSize);
              }
            }}
            autoHeight
            sx={{
              cursor: "default",
              "&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell": {
                py: 1,
              },
              "&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell": {
                py: "15px",
              },
              "&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell": {
                py: "22px",
              },
            }}
            sortingOrder={["asc", "desc"]}
          />
        </Box>
      </Box>
    </MyModal>
  );
}
